import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import AddressAutocomplete from '../../components/address/AddressAutocomplete';
import type { PlaceAddress } from '../../services/places';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Users, ArrowLeft, Check, Lock, MapPin, Plus, Trash2, Star, Truck, ExternalLink } from 'lucide-react';
import nurseryService from '../../services/nursery';
import orchardService from '../../services/orchard';
import nurseryAccountsService from '../../services/nurseryAccounts';
import type { AccountRules } from '../../types/nurseryAccounts';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { AU_STATES, isPlusCode, mapsUrl } from '../../utils/address';
import { pickDeliveryZone } from '../../utils/deliveryZones';
import { errorMessage } from '../../utils/apiResponse';
import type {
  NurseryCustomer,
  NurseryCustomerTier,
  NurseryDeliveryZone,
  NurseryPriceList,
  NurseryCustomerAddress,
  CreateCustomerPayload,
} from '../../types/nursery';

const addressSchema = z.object({
  id: z.number().optional(),
  label: z.string().optional(),
  line1: z.string().optional(),
  line2: z.string().optional(),
  suburb: z.string().optional(),
  state: z.string().optional(),
  postcode: z.string().optional(),
  country: z.string().optional(),
  plus_code: z.string().optional(),
  contact_name: z.string().optional(),
  contact_phone: z.string().optional(),
  instructions: z.string().optional(),
  is_default: z.boolean().optional(),
  // Coordinates from Places, and the address they were taken for. They are
  // sent only while the address still reads the same: a street retyped by
  // hand after picking a suggestion must not keep the old pin, or the stop
  // lands somewhere else on the despatch map.
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  geo_for: z.string().optional(),
});

type AddressForm = z.infer<typeof addressSchema>;

const geoKey = (a: Pick<AddressForm, 'line1' | 'suburb' | 'postcode'>) =>
  [a.line1, a.suburb, a.postcode].map((v) => (v ?? '').trim().toLowerCase()).join('|');

// The fields that make an address an address. A card with none of them
// filled in is dropped on save rather than refused — a pickup-only customer
// has no delivery address, and a blank card must not block the form.
const STREET_CONTENT: (keyof AddressForm)[] = ['line1', 'line2', 'suburb', 'postcode', 'plus_code'];
const hasStreet = (a: AddressForm) => STREET_CONTENT.some(k => String(a[k] ?? '').trim());
// A state on its own is kept (Rob, 2026-10-15): a head office with no street
// on file still needs its state, since its stores take it for quarantine. It
// used to be left out here, so picking only a state dropped the card on save.
// That is also why a new card starts with no state rather than VIC.
const isBlankAddress = (a: AddressForm) => !hasStreet(a) && !a.state?.trim();
const isStateOnly = (a: AddressForm) => !hasStreet(a) && Boolean(a.state?.trim());
const isAustralia = (a: AddressForm) => !a.country?.trim() || a.country.trim().toLowerCase() === 'australia';

function checkAddress(a: AddressForm, path: (string | number)[], ctx: z.RefinementCtx) {
  if (isBlankAddress(a) || isStateOnly(a)) return;
  if (!a.line1?.trim() && !a.plus_code?.trim()) {
    ctx.addIssue({ code: 'custom', path: [...path, 'line1'], message: 'Enter a street address or a plus code' });
  }
  if (!a.suburb?.trim()) {
    ctx.addIssue({ code: 'custom', path: [...path, 'suburb'], message: 'Suburb or town is required' });
  }
  const pc = a.postcode?.trim() ?? '';
  if (isAustralia(a)) {
    if (!/^\d{4}$/.test(pc)) {
      ctx.addIssue({ code: 'custom', path: [...path, 'postcode'], message: 'A 4-digit postcode is required' });
    }
  }
  if (a.plus_code?.trim() && !isPlusCode(a.plus_code)) {
    ctx.addIssue({ code: 'custom', path: [...path, 'plus_code'], message: 'Not a plus code — it looks like 4RJ7+2V or 4RJ74RJ7+2V' });
  }
}

const customerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  linked_org_id: z.number().optional(),
  customer_tier_id: z.number().optional(),
  contact_name: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  abn: z.string().optional(),
  notes: z.string().optional(),
  // Account. 'inherit' means: take it from the head office, else the default.
  parent_customer_id: z.number().optional(),
  bills_to_parent: z.boolean(),
  customer_code: z.string().max(50, 'Keep the number to 50 characters').optional(),
  terms_mode: z.enum(['inherit', 'days', 'eom_days']),
  payment_terms_days: z.number().optional(),
  po_required: z.enum(['inherit', 'yes', 'no']),
  credit_limit: z.number().min(0, 'A credit limit cannot be negative').optional(),
  // Pays before its orders are despatched (Account rules). Only whoever pays carries it.
  pay_before_despatch: z.boolean(),
  // Pays a deposit on orders for trees not ready, at its own % or the organisation's.
  deposit_required: z.boolean(),
  deposit_pct: z.number().gt(0, 'A deposit is more than 0%').max(100, 'A deposit is at most 100%').optional(),
  invoice_delivery: z.enum(['inherit', 'email', 'portal', 'none']),
  // The farm its orders are despatched from. Blank = the head office's.
  despatch_farm_id: z.number().optional(),
  // The one list it is priced from. Blank = the head office's, else the default.
  price_list_id: z.number().optional(),
  delivery: z.array(addressSchema),
  billing_same_as_delivery: z.boolean(),
  billing: addressSchema,
}).superRefine((data, ctx) => {
  if (data.terms_mode !== 'inherit') {
    const d = data.payment_terms_days;
    if (d === undefined || !Number.isInteger(d) || d < 0 || d > 365) {
      ctx.addIssue({ code: 'custom', path: ['payment_terms_days'], message: 'Enter a whole number of days, from 0 to 365' });
    }
  }
  data.delivery.forEach((a, i) => checkAddress(a, ['delivery', i], ctx));
  if (!data.billing_same_as_delivery) checkAddress(data.billing, ['billing'], ctx);
});

type CustomerFormData = z.infer<typeof customerSchema>;

// An empty number input / unselected <select> yields '' — with valueAsNumber
// that becomes NaN, which fails z.number().optional() and silently blocks
// submit. Coerce blanks to undefined instead.
const optionalNumber = (v: unknown) =>
  v === '' || v === null || v === undefined ? undefined : Number(v);

const emptyAddress = (isDefault = false): AddressForm => ({
  label: '', line1: '', line2: '', suburb: '', state: '', postcode: '', country: 'Australia',
  plus_code: '', contact_name: '', contact_phone: '', instructions: '', is_default: isDefault,
});

const toForm = (a: NurseryCustomerAddress): AddressForm => ({
  id: a.id,
  label: a.label ?? '',
  line1: a.line1 ?? '',
  line2: a.line2 ?? '',
  suburb: a.suburb ?? '',
  state: a.state ?? '',
  postcode: a.postcode ?? '',
  country: a.country ?? 'Australia',
  plus_code: a.plus_code ?? '',
  contact_name: a.contact_name ?? '',
  contact_phone: a.contact_phone ?? '',
  instructions: a.instructions ?? '',
  is_default: Boolean(a.is_default),
  lat: a.lat ?? null,
  lng: a.lng ?? null,
  geo_for: a.lat != null ? geoKey({ line1: a.line1 ?? '', suburb: a.suburb ?? '', postcode: a.postcode ?? '' }) : undefined,
});

const toPayload = (a: AddressForm, type: 'billing' | 'delivery'): NurseryCustomerAddress => ({
  ...(a.id ? { id: a.id } : {}),
  address_type: type,
  label: a.label?.trim() || null,
  line1: a.line1?.trim() || null,
  line2: a.line2?.trim() || null,
  suburb: a.suburb?.trim() || null,
  state: a.state?.trim() || null,
  postcode: a.postcode?.trim() || null,
  country: a.country?.trim() || 'Australia',
  plus_code: a.plus_code?.trim() || null,
  contact_name: a.contact_name?.trim() || null,
  contact_phone: a.contact_phone?.trim() || null,
  instructions: a.instructions?.trim() || null,
  is_default: type === 'delivery' ? Boolean(a.is_default) : false,
  ...(a.geo_for && a.geo_for === geoKey(a) ? { lat: a.lat ?? null, lng: a.lng ?? null } : { lat: null, lng: null }),
});

function money(n: number): string {
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const labelCls = 'block text-sm font-medium text-neutral-700 mb-1';

type AddressPath = 'billing' | `delivery.${number}`;

interface AddressFieldsProps {
  path: AddressPath;
  register: UseFormRegister<CustomerFormData>;
  errors?: FieldErrors<AddressForm>;
  value: AddressForm;
  /** Delivery-only extras: site contact and instructions for the driver. */
  delivery?: boolean;
  /** A suggestion was picked in the street field: fill the rest from it. */
  onPick: (address: PlaceAddress) => void;
}

function AddressFields({ path, register, errors, value, delivery, onPick }: AddressFieldsProps) {
  const url = mapsUrl(value);
  const err = (k: keyof AddressForm) =>
    errors?.[k]?.message ? <p className="text-xs text-red-500 mt-1">{String(errors[k]?.message)}</p> : null;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className={labelCls}>Street address</label>
          <AddressAutocomplete
            inputProps={{ ...register(`${path}.line1`), autoComplete: 'address-line1' }}
            onPick={onPick}
            className={inputCls}
            placeholder="Start typing, e.g. 1250 Midland Hwy"
          />
          {err('line1')}
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Address line 2</label>
          <input type="text" {...register(`${path}.line2`)} className={inputCls} placeholder="Unit, shed, gate — optional" autoComplete="address-line2" />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="col-span-2">
          <label className={labelCls}>Suburb / town</label>
          <input type="text" {...register(`${path}.suburb`)} className={inputCls} autoComplete="address-level2" />
          {err('suburb')}
        </div>
        <div>
          <label htmlFor={`${path}-state`} className={labelCls}>State</label>
          <select id={`${path}-state`} {...register(`${path}.state`)} className={inputCls} autoComplete="address-level1">
            <option value="">—</option>
            {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Postcode</label>
          <input type="text" inputMode="numeric" maxLength={10} {...register(`${path}.postcode`)} className={inputCls} autoComplete="postal-code" />
          {err('postcode')}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Country</label>
          <input type="text" {...register(`${path}.country`)} className={inputCls} autoComplete="country-name" />
        </div>
        <div>
          <label className={labelCls}>Google Maps plus code</label>
          <input
            type="text"
            {...register(`${path}.plus_code`)}
            className={`${inputCls} font-mono uppercase`}
            placeholder="e.g. 4RJ7+2V Tatura"
          />
          {err('plus_code') ?? (
            <p className="text-xs text-neutral-500 mt-1">
              Filled from the address you pick. For a spot the address doesn't find — a shed or a paddock gate — drop a pin in Google Maps and copy the code shown with it.
            </p>
          )}
        </div>
      </div>
      {delivery && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Site contact</label>
              <input type="text" {...register(`${path}.contact_name`)} className={inputCls} placeholder="Who takes the delivery" />
            </div>
            <div>
              <label className={labelCls}>Site phone</label>
              <input type="tel" {...register(`${path}.contact_phone`)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Delivery instructions</label>
            <textarea {...register(`${path}.instructions`)} rows={2} className={`${inputCls} resize-none`} placeholder="Gate code, where to unload, truck access…" />
          </div>
        </>
      )}
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
          <MapPin className="h-4 w-4 shrink-0" /> Check on Google Maps <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
      )}
    </div>
  );
}

function ZoneLine({ zones, tierId, address }: { zones: NurseryDeliveryZone[] | null; tierId?: number; address: AddressForm }) {
  if (zones === null) return null;
  const pc = address.postcode?.trim() ?? '';
  if (!/^\d{4}$/.test(pc) && isAustralia(address)) return null;
  if (zones.length === 0) {
    return (
      <p className="text-xs text-neutral-500 flex items-start gap-1.5">
        <Truck className="h-4 w-4 shrink-0" />
        <span>No delivery zones are set up yet, so no rate applies. <Link to="/nursery/delivery-zones" className="text-primary-600 hover:underline">Set up zones</Link></span>
      </p>
    );
  }
  const zone = pickDeliveryZone(zones, tierId, pc);
  if (!zone) {
    return (
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
        <Truck className="h-4 w-4 shrink-0" />
        <span>No delivery zone covers {pc || 'this address'} at this customer's tier — an order to it can't be quoted for delivery.</span>
      </p>
    );
  }
  const free = zone.free_over_subtotal != null ? `, free over ${money(Number(zone.free_over_subtotal))}` : '';
  return (
    <p className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
      <Truck className="h-4 w-4 shrink-0" />
      <span>Delivery zone <span className="font-medium">{zone.name}</span>: {money(Number(zone.flat_fee))}{free} ex GST</span>
    </p>
  );
}

/** An ABN is 11 digits and an ACN 9; the server judges the same way. */
const hasAbnDigits = (abn: string | undefined) => (abn || '').replace(/\D/g, '').length >= 9;

/** What the credit limit does, by the organisation's account rules. */
function creditHint(rules: AccountRules | null, isAdmin: boolean): string {
  const what = !rules || rules.credit_action === 'warn'
    ? 'Going over it shows a warning on the order. It never stops one.'
    : rules.credit_action === 'hold'
      ? 'Going over it holds the order at despatch until it is paid or released.'
      : 'Going over it refuses a new order until it is released.';
  const parts = ['The limit is inc GST, as balances are.', what];
  if (rules?.default_credit_limit != null) parts.push(`Blank uses the default of ${money(rules.default_credit_limit)}.`);
  if (rules?.max_credit_limit != null && !isAdmin) parts.push(`The most you can set is ${money(rules.max_credit_limit)}.`);
  return parts.join(' ');
}

/** What the account section needs to know about the customer being edited. */
type Existing = Pick<NurseryCustomer, 'site_count' | 'parent_customer_id' | 'parent_name'>;

function CustomerForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  // "Add a site" on a head office's page arrives with ?parent_id=.
  const presetParent = !isEdit && Number(searchParams.get('parent_id')) > 0 ? Number(searchParams.get('parent_id')) : undefined;
  const { hasAccess, loading: licenseLoading } = useModuleLicense('accounts', ['user', 'admin']);

  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [priceLists, setPriceLists] = useState<NurseryPriceList[]>([]);
  const [zones, setZones] = useState<NurseryDeliveryZone[] | null>(null);
  // null until loaded: the select is only rendered once its options exist,
  // so the stored head office is selected rather than silently dropped.
  const [headOffices, setHeadOffices] = useState<NurseryCustomer[] | null>(null);
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [existing, setExisting] = useState<Existing | null>(null);
  // The organisation's account rules: what a new customer starts on, and how
  // the credit limit is used. A failed read leaves the form as it was.
  const [rules, setRules] = useState<AccountRules | null>(null);
  const [wasPrepay, setWasPrepay] = useState(false);
  const { hasAccess: isAccountsAdmin } = useModuleLicense('accounts', 'admin');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: '',
      parent_customer_id: presetParent,
      bills_to_parent: true,
      terms_mode: 'inherit',
      po_required: 'inherit',
      pay_before_despatch: false,
      deposit_required: false,
      invoice_delivery: 'inherit',
      delivery: [emptyAddress(true)],
      billing_same_as_delivery: true,
      billing: emptyAddress(),
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'delivery' });

  useEffect(() => {
    nurseryService.getCustomerTiers().then(res => {
      if (res.success) setTiers(res.data || []);
    });
    nurseryService.getPriceLists()
      .then(res => setPriceLists(res.success ? (res.data || []).filter(l => l.status !== 'archived') : []))
      .catch(() => setPriceLists([]));
    nurseryService.getDeliveryZones().then(res => {
      if (res.success) setZones(res.data || []);
    }).catch(() => { /* the zone preview is a nicety; the form works without it */ });
    nurseryService.getCustomers({ top_level: true, active: true })
      .then(res => setHeadOffices(res.success ? res.data || [] : []))
      .catch(() => setHeadOffices([]));
    orchardService.getFarms(1, 100)
      .then(res => setFarms(res.success ? (res.data?.farms || []).map(f => ({ id: f.id, name: f.name })) : []))
      .catch(() => { /* the despatch farm can be set later; the form works without the list */ });
  }, []);

  useEffect(() => {
    nurseryAccountsService.getAccountRules()
      .then(res => {
        if (!res.success || !res.data) return;
        setRules(res.data.settings);
        // A new customer starts on what the organisation chose; an edit keeps its own.
        if (!isEdit && res.data.settings.new_customer_prepay) setValue('pay_before_despatch', true);
      })
      .catch(() => { /* the rules only shape hints; the server applies them */ });
  }, [isEdit, setValue]);

  useEffect(() => {
    if (!isEdit) return;
    nurseryService.getCustomer(Number(id)).then(res => {
      if (res.success && res.data) {
        const c = res.data;
        setWasPrepay(c.pay_before_despatch === true);
        const addresses = c.addresses ?? [];
        const delivery = addresses.filter(a => a.address_type === 'delivery').map(toForm);
        const billing = addresses.find(a => a.address_type === 'billing');
        reset({
          name: c.name,
          linked_org_id: c.linked_org_id ?? undefined,
          customer_tier_id: c.customer_tier_id ?? undefined,
          contact_name: c.contact_name ?? undefined,
          email: c.email ?? undefined,
          phone: c.phone ?? undefined,
          abn: c.abn ?? undefined,
          notes: c.notes ?? undefined,
          parent_customer_id: c.parent_customer_id ?? undefined,
          bills_to_parent: c.bills_to_parent !== false,
          customer_code: c.customer_code ?? '',
          terms_mode: c.payment_terms_type ?? 'inherit',
          payment_terms_days: c.payment_terms_type != null && c.payment_terms_days != null ? Number(c.payment_terms_days) : undefined,
          po_required: c.po_required == null ? 'inherit' : c.po_required ? 'yes' : 'no',
          credit_limit: c.credit_limit != null && c.credit_limit !== '' ? Number(c.credit_limit) : undefined,
          pay_before_despatch: c.pay_before_despatch === true,
          deposit_required: c.deposit_required === true,
          deposit_pct: c.deposit_pct != null && c.deposit_pct !== '' ? Number(c.deposit_pct) : undefined,
          invoice_delivery: c.invoice_delivery ?? 'inherit',
          despatch_farm_id: c.despatch_farm_id ?? undefined,
          price_list_id: c.price_list_id ?? undefined,
          delivery: delivery.length ? delivery : [emptyAddress(true)],
          billing_same_as_delivery: Boolean(c.billing_same_as_delivery),
          billing: billing ? toForm(billing) : emptyAddress(),
        });
        setExisting({ site_count: c.site_count, parent_customer_id: c.parent_customer_id, parent_name: c.parent_name });
      }
      setLoading(false);
    });
  }, [id, isEdit, reset]);

  const delivery = watch('delivery');
  const billing = watch('billing');
  const sameAsDelivery = watch('billing_same_as_delivery');
  const tierId = watch('customer_tier_id');
  const parentId = watch('parent_customer_id');
  const billsToParent = watch('bills_to_parent');
  const termsMode = watch('terms_mode');
  const abn = watch('abn');
  const depositRequired = watch('deposit_required');

  const hasParent = Boolean(parentId);
  const defaultList = priceLists.find(l => l.is_default);
  const hasSites = (existing?.site_count ?? 0) > 0;
  // The stored head office stays in the list even if it has since been made
  // inactive, so opening and saving the form does not quietly detach the site.
  const officeOptions: Pick<NurseryCustomer, 'id' | 'name' | 'customer_code'>[] =
    (headOffices ?? []).filter(o => o.id !== Number(id));
  if (existing?.parent_customer_id && !officeOptions.some(o => o.id === existing.parent_customer_id)) {
    officeOptions.unshift({ id: existing.parent_customer_id, name: existing.parent_name || 'Current head office' });
  }
  const inheritFrom = hasParent ? 'Use the head office’s' : null;

  // The plus code each address's last pick filled in, so a new pick can replace it.
  const placedCodes = useRef<Record<string, string>>({});

  const fillFromPlace = (path: AddressPath, a: PlaceAddress) => {
    const opts = { shouldDirty: true, shouldValidate: true };
    setValue(`${path}.line1`, a.line1 ?? '', opts);
    if (a.line2) setValue(`${path}.line2`, a.line2, opts);
    setValue(`${path}.suburb`, a.suburb ?? '', opts);
    setValue(`${path}.state`, a.state ?? '', opts);
    setValue(`${path}.postcode`, a.postcode ?? '', opts);
    setValue(`${path}.country`, a.country ?? 'Australia', opts);
    setValue(`${path}.lat`, a.lat ?? null, opts);
    setValue(`${path}.lng`, a.lng ?? null, opts);
    setValue(`${path}.geo_for`, a.lat != null ? geoKey({ line1: a.line1 ?? '', suburb: a.suburb ?? '', postcode: a.postcode ?? '' }) : undefined, opts);
    // Every pick fills the plus code: on a farm the street number finds the
    // letterbox, not the shed, and a driver needs the spot. Never over one
    // somebody typed — only into a blank field, or over the code the last
    // pick put there.
    const current = getValues(`${path}.plus_code`)?.trim() ?? '';
    if (a.plus_code && (!current || current === placedCodes.current[path])) {
      setValue(`${path}.plus_code`, a.plus_code, opts);
      placedCodes.current[path] = a.plus_code;
    }
  };

  const makeDefault = (index: number) => {
    delivery.forEach((_, i) => setValue(`delivery.${i}.is_default`, i === index, { shouldDirty: true }));
  };

  const removeDelivery = (index: number) => {
    const wasDefault = delivery[index]?.is_default;
    remove(index);
    // The survivor list is re-read after removal; hand the default on so there
    // is always exactly one.
    if (wasDefault && delivery.length > 1) {
      setValue(`delivery.0.is_default`, true, { shouldDirty: true });
    }
  };

  const onSubmit = async (data: CustomerFormData) => {
    setSubmitting(true);
    setError(null);
    try {
      const deliveryRows = data.delivery.filter(a => !isBlankAddress(a));
      if (deliveryRows.length && !deliveryRows.some(a => a.is_default)) deliveryRows[0].is_default = true;
      const addresses: NurseryCustomerAddress[] = deliveryRows.map(a => toPayload(a, 'delivery'));
      if (!data.billing_same_as_delivery && !isBlankAddress(data.billing)) {
        addresses.push(toPayload(data.billing, 'billing'));
      }
      const payload: CreateCustomerPayload = {
        name: data.name,
        ...(data.linked_org_id ? { linked_org_id: data.linked_org_id } : {}),
        ...(data.customer_tier_id ? { customer_tier_id: data.customer_tier_id } : {}),
        ...(data.contact_name ? { contact_name: data.contact_name } : {}),
        ...(data.email ? { email: data.email } : {}),
        ...(data.phone ? { phone: data.phone } : {}),
        ...(data.abn ? { abn: data.abn } : {}),
        ...(data.notes ? { notes: data.notes } : {}),
        // Anything left to inherit is sent as null, so clearing a setting
        // really clears it.
        parent_customer_id: data.parent_customer_id ?? null,
        bills_to_parent: data.parent_customer_id ? data.bills_to_parent : true,
        customer_code: data.customer_code?.trim() || null,
        payment_terms_type: data.terms_mode === 'inherit' ? null : data.terms_mode,
        payment_terms_days: data.terms_mode === 'inherit' ? null : data.payment_terms_days ?? null,
        po_required: data.po_required === 'inherit' ? null : data.po_required === 'yes',
        // The limit belongs to whoever pays, so a site paid for by its head
        // office carries none of its own.
        credit_limit: data.parent_customer_id && data.bills_to_parent ? null : data.credit_limit ?? null,
        pay_before_despatch: data.parent_customer_id && data.bills_to_parent ? null : data.pay_before_despatch,
        // Deposits belong to whoever pays too.
        deposit_required: data.parent_customer_id && data.bills_to_parent ? null : data.deposit_required,
        deposit_pct: data.parent_customer_id && data.bills_to_parent ? null : data.deposit_pct ?? null,
        invoice_delivery: data.invoice_delivery === 'inherit' ? null : data.invoice_delivery,
        despatch_farm_id: data.despatch_farm_id ?? null,
        price_list_id: data.price_list_id ?? null,
        billing_same_as_delivery: data.billing_same_as_delivery,
        addresses,
      };
      const res = isEdit
        ? await nurseryService.updateCustomer(Number(id), payload)
        : await nurseryService.createCustomer(payload);
      if (res.success) {
        const savedId = res.data?.id ?? (isEdit ? Number(id) : null);
        navigate(savedId ? `/accounts/customers/${savedId}` : '/accounts/customers');
      } else {
        setError(errorMessage(res, 'Failed to save customer'));
      }
    } catch (e) {
      setError(errorMessage(e, 'Failed to save customer'));
    } finally {
      setSubmitting(false);
    }
  };

  if (licenseLoading || loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-64 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Accounts licence required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Managing customers requires an active <span className="font-medium">Accounts</span> licence seat.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/accounts/customers')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to customers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isEdit ? 'Edit Customer' : 'New Customer'}
        icon={<Users className="h-6 w-6 text-white" />}
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-5">
            <div>
              <label className={labelCls}>
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input type="text" {...register('name')} className={inputCls} placeholder="e.g. Green Valley Orchards" />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Pricing Tier</label>
                <select {...register('customer_tier_id', { setValueAs: optionalNumber })} className={inputCls}>
                  <option value="">{hasParent ? 'Use the head office’s tier' : 'No tier'}</option>
                  {tiers.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cust-price-list" className={labelCls}>Price List</label>
                <select id="cust-price-list" {...register('price_list_id', { setValueAs: optionalNumber })} className={inputCls}>
                  <option value="">
                    {hasParent
                      ? 'Use the head office’s list'
                      : `Default list${defaultList ? ` (${defaultList.name})` : ''}`}
                  </option>
                  {priceLists.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.name}{l.status === 'draft' ? ' (draft)' : ''}{l.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Linked Organisation ID</label>
                <input
                  type="number"
                  {...register('linked_org_id', { setValueAs: optionalNumber })}
                  className={inputCls}
                  placeholder="Optional — if a platform org"
                />
              </div>
              <div>
                <label className={labelCls}>ABN/ACN</label>
                <input
                  type="text"
                  {...register('abn')}
                  className={inputCls}
                  placeholder="e.g. 12 345 678 901 or ACN 123 456 789"
                />
              </div>
              <div>
                <label className={labelCls}>Contact Name</label>
                <input type="text" {...register('contact_name')} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input type="email" {...register('email')} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input type="text" {...register('phone')} className={inputCls} />
              </div>
            </div>

            <div>
              <label className={labelCls}>Notes</label>
              <textarea {...register('notes')} rows={2} className={`${inputCls} resize-none`} />
            </div>
          </div>

          <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-4">
            <div>
              <h2 className="text-base font-semibold text-neutral-800">Account</h2>
              <p className="text-sm text-neutral-500">
                Who pays, and on what terms. A site takes each of these from its head office unless you set it here.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="cust-parent" className={labelCls}>Head office</label>
                {headOffices === null ? (
                  <div className="h-[38px] bg-neutral-100 rounded-lg animate-pulse" />
                ) : (
                  <select
                    id="cust-parent"
                    {...register('parent_customer_id', { setValueAs: optionalNumber })}
                    className={`${inputCls} disabled:bg-neutral-100 disabled:text-neutral-500`}
                    disabled={hasSites}
                  >
                    <option value="">None — this is its own account</option>
                    {officeOptions.map(o => (
                      <option key={o.id} value={o.id}>{o.name}{o.customer_code ? ` (#${o.customer_code})` : ''}</option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-neutral-500 mt-1">
                  {hasSites
                    ? `This customer is a head office with ${existing?.site_count} site${existing?.site_count === 1 ? '' : 's'}, so it cannot itself be a site. There are only two levels: a head office and its sites.`
                    : 'Choose one to make this customer a site (a store or branch) of that head office.'}
                </p>
              </div>
              <div>
                <label htmlFor="cust-code" className={labelCls}>Store or account number</label>
                <input id="cust-code" type="text" {...register('customer_code')} className={inputCls} placeholder="e.g. 1234" />
                {errors.customer_code
                  ? <p className="text-xs text-red-500 mt-1">{errors.customer_code.message}</p>
                  : (
                    <p className="text-xs text-neutral-500 mt-1">
                      The customer’s own number for this store or account. It prints on invoices and is how a remittance names the store.
                    </p>
                  )}
              </div>
            </div>

            {hasParent && (
              <div>
                <label htmlFor="cust-bills-to-parent" className="inline-flex items-start gap-2 text-sm text-neutral-700 cursor-pointer">
                  <input id="cust-bills-to-parent" type="checkbox" {...register('bills_to_parent')} className="mt-0.5 h-4 w-4 rounded border-neutral-300" />
                  <span>The head office pays this site’s invoices</span>
                </label>
                <p className="text-xs text-neutral-500 mt-1">
                  Turn this off for a site that pays its own bills. It still gets the head office’s prices.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="cust-terms" className={labelCls}>Payment terms</label>
                <select id="cust-terms" {...register('terms_mode')} className={inputCls}>
                  <option value="inherit">{inheritFrom ?? 'Use the default (30 days)'}</option>
                  <option value="days">Days after the invoice date</option>
                  <option value="eom_days">Days after the end of the invoice month</option>
                </select>
              </div>
              {termsMode !== 'inherit' && (
                <div>
                  <label htmlFor="cust-terms-days" className={labelCls}>Days</label>
                  <input
                    id="cust-terms-days"
                    type="number" min="0" max="365" step="1" inputMode="numeric"
                    {...register('payment_terms_days', { setValueAs: optionalNumber })}
                    className={inputCls}
                    placeholder={termsMode === 'eom_days' ? 'e.g. 60' : 'e.g. 30'}
                  />
                  {errors.payment_terms_days && <p className="text-xs text-red-500 mt-1">{errors.payment_terms_days.message}</p>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="cust-po" className={labelCls}>Purchase order number</label>
                <select id="cust-po" {...register('po_required')} className={inputCls}>
                  <option value="inherit">{inheritFrom ?? 'Use the default (not required)'}</option>
                  <option value="yes">Required on every order</option>
                  <option value="no">Not required</option>
                </select>
              </div>
              <div>
                <label htmlFor="cust-delivery" className={labelCls}>How invoices are delivered</label>
                <select id="cust-delivery" {...register('invoice_delivery')} className={inputCls}>
                  <option value="inherit">{inheritFrom ?? 'Use the default (email)'}</option>
                  <option value="email">Email</option>
                  <option value="portal">The customer’s own portal (nothing is emailed; copies are kept)</option>
                  <option value="none">Not sent</option>
                </select>
              </div>
            </div>

            {farms.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="cust-despatch-farm" className={labelCls}>Despatched from</label>
                  <select id="cust-despatch-farm" {...register('despatch_farm_id', { setValueAs: optionalNumber })} className={inputCls}>
                    <option value="">{inheritFrom ?? 'No farm set'}</option>
                    {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  <p className="text-xs text-neutral-500 mt-1">
                    An imported purchase order goes on the cross dock load at this farm.
                  </p>
                </div>
              </div>
            )}

            {hasParent && billsToParent ? (
              <p className="text-xs text-neutral-500">
                No credit limit or deposit is set here. Both belong to whoever pays, which for this site is its head office.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="cust-credit" className={labelCls}>Credit limit ($)</label>
                  <input
                    id="cust-credit"
                    type="number" min="0" step="0.01" inputMode="decimal"
                    {...register('credit_limit', { setValueAs: optionalNumber })}
                    className={inputCls}
                    placeholder="Leave blank for none"
                  />
                  {errors.credit_limit
                    ? <p className="text-xs text-red-500 mt-1">{errors.credit_limit.message}</p>
                    : <p className="text-xs text-neutral-500 mt-1 break-words">{creditHint(rules, isAccountsAdmin)}</p>}
                </div>
                <div className="min-w-0">
                  <span className={labelCls}>Payment</span>
                  <label htmlFor="cust-prepay" className="flex items-start gap-2 text-sm text-neutral-800 break-words">
                    <input
                      id="cust-prepay"
                      type="checkbox"
                      {...register('pay_before_despatch')}
                      disabled={wasPrepay && !isAccountsAdmin}
                      className="mt-0.5 shrink-0"
                    />
                    <span>Pays before despatch</span>
                  </label>
                  <p className="text-xs text-neutral-500 mt-1 break-words">
                    {wasPrepay && !isAccountsAdmin
                      ? 'Only an Accounts Admin can give this customer an account.'
                      : 'Its orders are not despatched until they are paid, or an Accounts Admin releases them.'}
                  </p>
                  {rules?.no_abn_action === 'prepay' && !hasAbnDigits(abn) && (
                    <p className="text-xs text-amber-700 mt-1 break-words">
                      No ABN is entered, so this customer pays before despatch whatever is ticked here.
                    </p>
                  )}
                </div>
                <div className="min-w-0 sm:col-span-2">
                  <span className={labelCls}>Deposits</span>
                  <label htmlFor="cust-deposit" className="flex items-start gap-2 text-sm text-neutral-800 break-words">
                    <input id="cust-deposit" type="checkbox" {...register('deposit_required')} className="mt-0.5 shrink-0" />
                    <span>Pays a deposit on orders for trees not ready yet</span>
                  </label>
                  {depositRequired && (
                    <div className="mt-2 max-w-xs">
                      <label htmlFor="cust-deposit-pct" className={labelCls}>Deposit (% of trees ex GST)</label>
                      <input
                        id="cust-deposit-pct"
                        type="number" min="0" max="100" step="0.01" inputMode="decimal"
                        {...register('deposit_pct', { setValueAs: optionalNumber })}
                        className={inputCls}
                        placeholder={rules ? `${rules.deposit_pct}% (the organisation's)` : "The organisation's"}
                      />
                      {errors.deposit_pct && <p className="text-xs text-red-500 mt-1">{errors.deposit_pct.message}</p>}
                    </div>
                  )}
                  <p className="text-xs text-neutral-500 mt-1 break-words">
                    Each order is asked for a deposit invoice, sent to Xero. Its invoices take off the deposits paid.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-neutral-800">Delivery addresses</h2>
                <p className="text-sm text-neutral-500">The postcode picks the delivery zone and its rate. Leave blank for a pickup-only customer.</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => append(emptyAddress(fields.length === 0))}>
                <Plus className="h-4 w-4" /> Add address
              </Button>
            </div>

            {fields.map((field, i) => {
              const value = delivery[i] ?? emptyAddress();
              return (
                <div key={field.id} className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-4">
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="flex-1 min-w-[12rem]">
                      <label className={labelCls}>Name for this address</label>
                      <input
                        type="text"
                        {...register(`delivery.${i}.label`)}
                        className={inputCls}
                        placeholder="e.g. Home block, North farm"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-0 sm:pt-6">
                      {value.is_default ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-full px-2.5 py-1">
                          <Star className="h-3.5 w-3.5 fill-current" /> Default
                        </span>
                      ) : (
                        <Button type="button" variant="ghost" size="sm" onClick={() => makeDefault(i)}>
                          <Star className="h-4 w-4" /> Make default
                        </Button>
                      )}
                      {fields.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeDelivery(i)} aria-label="Remove this address">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <AddressFields
                    path={`delivery.${i}`}
                    register={register}
                    errors={errors.delivery?.[i]}
                    value={value}
                    delivery
                    onPick={(a) => fillFromPlace(`delivery.${i}`, a)}
                  />
                  <ZoneLine zones={zones} tierId={tierId} address={value} />
                </div>
              );
            })}
          </section>

          <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-4">
            <div>
              <h2 className="text-base font-semibold text-neutral-800">Billing address</h2>
              <label className="mt-2 inline-flex items-start gap-2 text-sm text-neutral-700 cursor-pointer">
                <input type="checkbox" {...register('billing_same_as_delivery')} className="mt-0.5 h-4 w-4 rounded border-neutral-300" />
                <span>Same as the default delivery address</span>
              </label>
            </div>
            {!sameAsDelivery && (
              <AddressFields path="billing" register={register} errors={errors.billing} value={billing} onPick={(a) => fillFromPlace('billing', a)} />
            )}
          </section>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          <div className="flex flex-wrap justify-between gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={() => navigate(
                isEdit ? `/accounts/customers/${id}` : presetParent ? `/accounts/customers/${presetParent}?tab=sites` : '/accounts/customers',
              )}
            >
              <ArrowLeft className="h-4 w-4" /> Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Customer'}
              {!submitting && <Check className="h-4 w-4" />}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}

export default CustomerForm;
